import { supabase } from '../lib/supabase'
import { AnnouncementService } from './announcements'

export interface SchoolAnnouncement {
  id: string
  school_id: string
  created_by: string
  title: string
  content: string
  image_path?: string
  url?: string
  target_grades: string[]
  target_classes: string[]
  target_group_ids: string[]
  published_at: string
  created_at: string
  updated_at: string
}

export interface SchoolAnnouncementInsert {
  school_id: string
  title: string
  content: string
  image_path?: string
  url?: string
  target_grades: string[]
  target_classes: string[]
  target_group_ids: string[]
  published_at: string
}

export interface SchoolAnnouncementUpdate {
  title?: string
  content?: string
  image_path?: string
  url?: string
  target_grades?: string[]
  target_classes?: string[]
  target_group_ids?: string[]
  published_at?: string
}

export class SchoolAnnouncementService {
  // Get all announcements for a school (including unpublished) — for teacher management
  static async getAllSchoolAnnouncements(schoolId: string): Promise<SchoolAnnouncement[]> {
    const { data, error } = await supabase
      .from('school_announcements')
      .select('*')
      .eq('school_id', schoolId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  // Get published announcements for a school — for student view
  static async getPublishedSchoolAnnouncements(schoolId: string): Promise<SchoolAnnouncement[]> {
    const { data, error } = await supabase
      .from('school_announcements')
      .select('*')
      .eq('school_id', schoolId)
      .lte('published_at', new Date().toISOString())
      .order('published_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  // Create new school announcement
  static async createSchoolAnnouncement(data: SchoolAnnouncementInsert): Promise<SchoolAnnouncement> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('ログインが必要です')

    const { data: result, error } = await supabase
      .from('school_announcements')
      .insert({ ...data, created_by: user.id })
      .select()
      .single()

    if (error) throw error
    return result
  }

  // Update school announcement
  static async updateSchoolAnnouncement(id: string, updates: SchoolAnnouncementUpdate): Promise<SchoolAnnouncement> {
    const { data, error } = await supabase
      .from('school_announcements')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Delete school announcement
  static async deleteSchoolAnnouncement(id: string): Promise<void> {
    const { error } = await supabase
      .from('school_announcements')
      .delete()
      .eq('id', id)

    if (error) throw error
  }

  // Image methods — delegate to AnnouncementService (same 'announcements' storage bucket)
  static async uploadImage(file: File): Promise<string> {
    return AnnouncementService.uploadImage(file)
  }

  static async deleteImage(imagePath: string): Promise<void> {
    return AnnouncementService.deleteImage(imagePath)
  }

  static getImageUrl(imagePath: string): string {
    return AnnouncementService.getImageUrl(imagePath)
  }
}

// Pure client-side helper: filter school announcements for a specific student
// Empty array means "no filter for this dimension" (show to everyone)
export function filterSchoolAnnouncementsForStudent(
  announcements: SchoolAnnouncement[],
  studentGrade: string | null,
  studentClass: string | null,
  studentGroupIds: string[]
): SchoolAnnouncement[] {
  return announcements.filter(ann => {
    const gradeOk =
      ann.target_grades.length === 0 ||
      (studentGrade != null && ann.target_grades.includes(studentGrade))
    const classOk =
      ann.target_classes.length === 0 ||
      (studentClass != null && ann.target_classes.includes(studentClass))
    const groupOk =
      ann.target_group_ids.length === 0 ||
      studentGroupIds.some(gid => ann.target_group_ids.includes(gid))
    return gradeOk && classOk && groupOk
  })
}
